import React, { useState, useEffect } from "react";
import Navbar from "../nav_bar/Navbar";
import "./LoginPage.css";
import { GoogleLogin } from "@react-oauth/google";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { emailLogin, googleLogin } from "../../services/userAuthService";
import axiosInstance from "../../api/axiosInstance";
import { jwtDecode } from "jwt-decode";
import { TERMS_VERSION } from "../../../common/constants";


const LoginPage = () => {
  const [step, setStep] = useState("login"); // login | otp
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [googleId, setGoogleId] = useState(null);
  const [isLinking, setIsLinking] = useState(false);
  const [picture, setProfilePicture] = useState(null);
  const [suspendReason, setSuspendReason] = useState("");

  const [alertMessage, setAlertMessage] = useState("");
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [showSuspendedModal, setShowSuspendedModal] = useState(false);

  const navigate = useNavigate();
  const { login, setIsSuspended } = useAuth();
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [showGoogleConsent, setShowGoogleConsent] = useState(false);
  const [pendingGoogleData, setPendingGoogleData] = useState(null);
  const [acceptedGoogleTerms, setAcceptedGoogleTerms] = useState(false);
  const [isLinkRequired, setIsLinkRequired] = useState(false);
  const [referralCode, setReferralCode] = useState(null);
  const [mobileNumber, setMobileNumber] = useState("");
  const [dialCode, setDialCode] = useState("+91");
  const [countryCodes, setCountryCodes] = useState([]);
  const [mobileError, setMobileError] = useState("");

  const showAlert = (message) => {
    console.log("[Alert]", message);
    setAlertMessage(message);
    setShowAlertModal(true);
  };

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BASE_URL}/public/country-codes`)
      .then(res => setCountryCodes(res.data?.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {

  const params = new URLSearchParams(window.location.search);
  const ref = params.get("ref");

  if (ref && ref.trim() !== "") {

    const normalizedRef = ref.trim().toUpperCase();

    console.info("[Referral] Detected:", normalizedRef);

    setReferralCode(normalizedRef);

    localStorage.setItem("referralCode", normalizedRef);

  } else {

    const storedRef = localStorage.getItem("referralCode");

    if (storedRef) {
      setReferralCode(storedRef.toUpperCase());
    }
  }

}, []);

  useEffect(() => {
    console.log("[LoginPage] useEffect triggered: step =", step, "timeZone =", userTimeZone);
  }, [step, userTimeZone]);

  // ================= EMAIL LOGIN =================
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    console.group("[Email Login]");
    console.log("Attempting login for:", loginData.email);

    try {
      const res = await emailLogin(loginData.email, loginData.password, userTimeZone);
      console.log("[Email Login] Response:", res.data);

      if (res.data?.status === "SUSPENDED" || res.data?.isSuspended) {
        console.log("[Email Login] User suspended:", res.data.reason);
        setIsSuspended(true);
        localStorage.setItem("isSuspended", "true");
        setSuspendReason(res.data.reason || "No reason provided");
        setShowSuspendedModal(true);
        setLoading(false);
        return;
      }

      console.log("[Email Login] Successful login. Token received.");
      login(res.data);
      navigate("/", { replace: true });
    } catch (err) {
      const backend = err.response?.data;
      console.warn("[Email Login] Error:", backend);

      if (backend?.message === "Your account has been suspended") {
        console.log("[Email Login] Suspended error caught.");
        setIsSuspended(true);
        localStorage.setItem("isSuspended", "true");
        setSuspendReason(backend.reason || "No reason provided");
        setShowSuspendedModal(true);
        setLoading(false);
        console.groupEnd();
        return;
      }

      showAlert(backend?.message || "Email login failed");
    } finally {
      console.groupEnd();
      setLoading(false);
    }
  };

// ================= GOOGLE LOGIN =================
const handleGoogleSuccess = async (cred) => {
  console.group("[Google Login]");

  const decoded = jwtDecode(cred.credential);

const googleProfile = {
  googleId: decoded.sub,
  email: decoded.email,
  name: decoded.name,
  picture: decoded.picture,
};

  if (!cred?.credential) {
    console.error("No Google credential received.");
    showAlert("Google authentication failed. Please try again.");
    console.groupEnd();
    return;
  }

  setLoading(true);
  console.log("Google credential received.");

  try {
    const res = await googleLogin(cred.credential, userTimeZone);

    console.log("[Google Login] Raw Response:", res.data);

    // Support wrapped or direct backend response
    const backend = res.data?.data ?? res.data;

    console.debug("[Google Login] Parsed Backend:", backend);

    // ================= SUSPENDED USER =================
    if (backend?.status === "SUSPENDED") {
      console.log("[Google Login] Suspended user detected.");

      setIsSuspended(true);
      localStorage.setItem("isSuspended", "true");
      setSuspendReason(backend.reason || "No reason provided");
      setShowSuspendedModal(true);
      return;
    }

    // ================= EXISTING GOOGLE USER =================
    if (backend?.userType === "EXISTING_USER" && backend?.token) {
      console.log("[Google Login] Existing user, logging in...");
      login(backend);
      navigate("/", { replace: true });
      return;
    }

    // ================= NEW GOOGLE USER =================
    if (backend?.userType === "NEW_USER") {
      console.log("[Google Login] New Google user, showing consent modal.");

      setPendingGoogleData({
        googleId: backend.googleId,
        email: backend.email,
        name: backend.name,
        picture: backend.picture || null,
      });

      setAcceptedGoogleTerms(false);
      setShowGoogleConsent(true);
      return;
    }

    // ================= FALLBACK =================
    showAlert("Login failed. Please try again.");

  } catch (err) {
    const backend = err.response?.data;

    console.error("[Google Login] Error:", backend || err.message);

    // ================= LINK REQUIRED (409 CASE) =================
if (backend?.status === "LINK_REQUIRED") {

  console.log("[Google Login] Account linking required.");

  const linkingData = {
    googleId: backend.googleId,
    email: backend.email,
    name: googleProfile.name,
    picture: googleProfile.picture || null,
  };

  setPendingGoogleData(linkingData);

  setEmail(linkingData.email);
  setGoogleId(linkingData.googleId);

  setIsLinkRequired(true);   // ✅ THIS ENABLES BUTTON

  showAlert(
    backend.message || "Email exists. Confirm account linking."
  );

  return;
}

    setIsLinkRequired(false);
    showAlert(backend?.message || "Google login failed");

  } finally {
    console.groupEnd();
    setLoading(false);
  }
};


  const handleGoogleCompleteLogin = async () => {
    console.group("[Google Complete Login]");
    console.log("Attempting to complete Google signup for:", pendingGoogleData?.email);

    if (!pendingGoogleData) {
      console.warn("No pending Google data found.");
      return;
    }

    if (!acceptedGoogleTerms) {
      showAlert("You must agree to the Terms of Service and Privacy Policy to continue.");
      return;
    }

    setLoading(true);

    try {
const res = await axiosInstance.post("/auth/google/complete", {
  googleId: pendingGoogleData.googleId,
  email: pendingGoogleData.email,
  name: pendingGoogleData.name,
  userTimeZone,
  picture: pendingGoogleData.picture,

  referralCode:
    referralCode || localStorage.getItem("referralCode"),

  termsAccepted: true,
  termsVersion: TERMS_VERSION,
  privacyAccepted: true,
  ...(mobileNumber ? { mobile_number: dialCode + mobileNumber, whatsapp_enabled: true } : {})
});
      console.log("[Google Complete Login] Signup success:", res.data);

      login(res.data);

      setShowGoogleConsent(false);
      setAcceptedGoogleTerms(false);
      setPendingGoogleData(null);

      navigate("/", { replace: true });
    } catch (err) {
      console.error("[Google Complete Login] Error:", err.response?.data || err.message);
      showAlert(err.response?.data?.message || "Google signup failed");
    } finally {
      console.groupEnd();
      setLoading(false);
    }
  };

  // ================= ACCOUNT LINKING =================
  const handleAccountLinking = async () => {
    setLoading(true);
    console.group("[Account Linking]");
    console.log("Linking account for email:", email);

    try {
      const res = await axiosInstance.post("/auth/google/link-account", {
        email,
        googleId,
       picture: pendingGoogleData?.picture || null,
        userTimeZone,
      });
      console.log("[Account Linking] OTP sent:", res.data);

      setShowAlertModal(false);
      setStep("otp");
    } catch (err) {
      console.error("[Account Linking] Error:", err.response?.data || err.message);
      showAlert(err.response?.data?.message || "Account linking failed");
    } finally {
      console.groupEnd();
      setLoading(false);
    }
  };

  // ================= OTP VERIFY =================
  const handleOtpVerify = async () => {
    if (!otp) {
      showAlert("Please enter OTP");
      return;
    }

    setLoading(true);
    console.group("[OTP Verify]");
    console.log("Verifying OTP for email:", email);

    try {
      const res = await axiosInstance.post("/auth/link/confirm", {
        email,
        otp,
        googleId,
        picture: pendingGoogleData?.picture || null,
        timeZone: userTimeZone,
      });
      console.log("[OTP Verify] OTP verified. Logging in...");

      login(res.data);
      navigate("/", { replace: true });
    } catch (err) {
      console.error("[OTP Verify] Error:", err.response?.data || err.message);
      showAlert(err.response?.data?.message || "Invalid OTP");
    } finally {
      console.groupEnd();
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <Navbar />
      <main className="login-wrapper">
        <div className="form-container">
          <h2>Welcome Back 👋</h2>

          {step === "login" ? (
            <>
              <p className="subtitle">Login to continue to your account</p>

              <div className="google-wrapper">
                <GoogleLogin onSuccess={handleGoogleSuccess} />
              </div>

              <div className="or-divider">OR</div>

              <form onSubmit={handleLogin}>
                <div className="input-group">
                  <input
                    type="email"
                    required
                    value={loginData.email}
                    onChange={(e) =>
                      setLoginData({ ...loginData, email: e.target.value })
                    }
                  />
                  <label>Email address</label>
                </div>

                <div className="input-group">
                  <input
                    type="password"
                    required
                    value={loginData.password}
                    onChange={(e) =>
                      setLoginData({ ...loginData, password: e.target.value })
                    }
                  />
                  <label>Password</label>
                </div>

                <button className="login-btn" type="submit" disabled={loading}>
                  {loading ? "Logging in..." : "Login"}
                </button>

                <div className="auth-links">
                  <Link to="/forgot-password" className="forgot-link">
                    Forgot password?
                  </Link>
                </div>
              </form>

              <p className="register-text">
                Don’t have an account?{" "}
                <Link
                  to="/register"
                  className="register-link"
                >
                  Create one
                </Link>
              </p>
            </>
          ) : (
            <>
              <p className="subtitle">
                Enter the OTP sent to <b>{email}</b>
              </p>

              <div className="input-group">
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  maxLength={6}
                />
                <label>OTP</label>
              </div>

              <button
                className="login-btn"
                onClick={handleOtpVerify}
                disabled={loading}
              >
                Verify & Continue
              </button>
            </>
          )}
        </div>
      </main>

      {/* ALERT MODAL */}
      {showAlertModal && (
        <div className="modal-overlay">
          <div className="modal-box">
            <h3>Alert</h3>
            <p>{alertMessage}</p>

            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setShowAlertModal(false)}
              >
                Close
              </button>

              {isLinkRequired && (
                <button
                  className="btn-primary"
                  onClick={handleAccountLinking}
                  disabled={loading}
                >
                  {loading ? "Generating OTP..." : "Link Account"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showSuspendedModal && (
        <div className="modal-overlay" onClick={() => setShowSuspendedModal(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <h3>Account Suspended</h3>
            <p>Your account has been suspended.</p>
            {suspendReason && (
              <p style={{ marginTop: "8px", fontWeight: "500" }}>
                <strong>Reason:</strong> {suspendReason}
              </p>
            )}
            <div className="modal-actions">
              <button
                className="btn-primary"
                onClick={() => {
                  setShowSuspendedModal(false);
                  setIsSuspended(false);
                  localStorage.removeItem("isSuspended");
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* GOOGLE CONSENT MODAL */}
      {showGoogleConsent && (
        <div className="modal-overlay">
          <div className="modal-box">
            <h3>Complete Your Registration</h3>
            <p>
              Welcome <b>{pendingGoogleData?.name}</b>
            </p>
            <p>Please accept our Terms to continue.</p>

            <div className="input-group mobile-input-group" style={{ marginTop: "15px", width: "100%" }}>
              <label className="mobile-label">Mobile Number (Optional)</label>
              <div className="mobile-row">
                <select
                  className="country-code-select"
                  value={dialCode}
                  onChange={(e) => setDialCode(e.target.value)}
                >
                  {countryCodes.length > 0 ? (
                    countryCodes.map((c) => (
                      <option key={c.id} value={c.dialCode}>
                        {c.isoCode} {c.dialCode}
                      </option>
                    ))
                  ) : (
                    <option value="+91">IN +91</option>
                  )}
                </select>
                <input
                  type="tel"
                  value={mobileNumber}
                  onChange={(e) => {
                    setMobileNumber(e.target.value.replace(/[^\d]/g, ""));
                    setMobileError("");
                  }}
                  placeholder="0000000000"
                  maxLength={15}
                  className="mobile-number-input"
                />
              </div>
              {mobileError && <span className="field-error">{mobileError}</span>}
            </div>

            <div className="input-group" style={{ marginTop: "15px" }}>
  <input
    type="text"
    value={referralCode || ""}
    onChange={(e) => {

      const code = e.target.value.toUpperCase();

      setReferralCode(code);

      if (code)
        localStorage.setItem("referralCode", code);
      else
        localStorage.removeItem("referralCode");

    }}
    placeholder=" "
    maxLength={12}
  />
  <label>Referral Code (Optional)</label>
</div>

            <div className="terms-container" style={{ marginTop: "15px" }}>
              <label className="terms-label">
                <input
                  type="checkbox"
                  checked={acceptedGoogleTerms}
                  onChange={(e) => setAcceptedGoogleTerms(e.target.checked)}
                  className="terms-checkbox"
                  aria-label="Accept Terms of Service and Privacy Policy"
                />
                <span>
                  I agree to the{" "}
                  <Link to="/terms-and-conditions" target="_blank" className="terms-link">
                    Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link to="/privacy-policy" target="_blank" className="terms-link">
                    Privacy Policy
                  </Link>.
                </span>
              </label>
            </div>

            <div className="modal-actions" style={{ marginTop: "20px" }}>
              <button
                className="login-btn"
                onClick={handleGoogleCompleteLogin}
                disabled={loading || !acceptedGoogleTerms}
              >
                {loading ? "Processing..." : "Accept & Continue"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ALERT MODAL */}
{showAlertModal && (
  <div className="modal-overlay">
    <div className="modal-box">
      <h3>Alert</h3>
      <p>{alertMessage}</p>

      <div className="modal-actions">
        <button
          className="btn-secondary"
          onClick={() => {
            setShowAlertModal(false);
            setIsLinkRequired(false);
          }}
        >
          Close
        </button>

        {isLinkRequired && (
          <button
            className="btn-primary"
            onClick={handleAccountLinking}
            disabled={loading}
          >
            {loading ? "Generating OTP..." : "Link Account"}
          </button>
        )}
      </div>
    </div>
  </div>
)}

    </div>
  );
};

export default LoginPage;
